import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { SiteNav, SiteFooter } from './HomePage';

const API = process.env.REACT_APP_BACKEND_URL;
const fadeIn = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const ContactPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', organization: '', role: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Please fill in all required fields'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/demo-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: 'contact', source: 'contact_page' })
      });
      if (res.ok) { setSent(true); toast.success('Message sent! We\'ll get back to you soon.'); }
      else toast.error('Failed to send. Please try again.');
    } catch (e) { toast.error('Connection error. Please try again.'); }
    setSending(false);
  };

  return (
    <div style={{ backgroundColor: '#F5F3EF', fontFamily: "'Manrope', sans-serif" }}>
      <SiteNav />
      
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
        <motion.div {...fadeIn} className="max-w-2xl">
          <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Get In Touch</span>
          <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-4xl md:text-6xl font-extrabold tracking-tighter leading-none text-[#2D2824] mb-5">
            We'd Love to<br/><span className="text-[#E86C3D]">Hear From You</span>
          </h1>
          <p className="text-base md:text-lg text-[#2D2824]/60 leading-relaxed">
            Whether you're exploring YAS Connect for your organization, have a question about the platform, 
            or just want to connect — we're here.
          </p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-5 gap-12">
          
          {/* Form */}
          <div className="md:col-span-3">
            {sent ? (
              <motion.div {...fadeIn} className="p-12 rounded-3xl bg-white border border-black/5 text-center">
                <div className="w-16 h-16 rounded-full bg-[#4ADE80]/15 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-[#4ADE80]" />
                </div>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-2xl font-bold text-[#2D2824] mb-3">Message Sent!</h2>
                <p className="text-[#2D2824]/60 mb-6">We'll get back to you within 24 hours. In the meantime, check out our AI features or request a demo.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => navigate('/ai')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full px-6 font-bold">Explore AI Features</Button>
                  <Button onClick={() => navigate('/demo')} className="rounded-full px-6 font-bold bg-[#2D2824]/5 text-[#2D2824] hover:bg-[#2D2824]/10 border border-[#2D2824]/10">Request Demo</Button>
                </div>
              </motion.div>
            ) : (
              <motion.form {...fadeIn} onSubmit={handleSubmit} className="p-8 rounded-3xl bg-white border border-black/5 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold text-[#2D2824]/70 mb-1.5 block">Your Name *</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="First and last name" className="rounded-xl border-[#2D2824]/10 focus:ring-[#E86C3D]/30 bg-[#F5F3EF]/50" required />
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-[#2D2824]/70 mb-1.5 block">Email Address *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@organization.com" className="rounded-xl border-[#2D2824]/10 focus:ring-[#E86C3D]/30 bg-[#F5F3EF]/50" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold text-[#2D2824]/70 mb-1.5 block">Organization</Label>
                    <Input value={form.organization} onChange={e => setForm({...form, organization: e.target.value})} placeholder="Your org name" className="rounded-xl border-[#2D2824]/10 focus:ring-[#E86C3D]/30 bg-[#F5F3EF]/50" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-[#2D2824]/70 mb-1.5 block">Your Role</Label>
                    <Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} placeholder="e.g. Program Director" className="rounded-xl border-[#2D2824]/10 focus:ring-[#E86C3D]/30 bg-[#F5F3EF]/50" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#2D2824]/70 mb-1.5 block">Message *</Label>
                  <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help..." rows={5} className="rounded-xl border-[#2D2824]/10 focus:ring-[#E86C3D]/30 bg-[#F5F3EF]/50" required />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full py-3 text-base font-bold" style={{ transition: 'background 0.2s ease' }}>
                  {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                </Button>
              </motion.form>
            )}
          </div>

          {/* Info */}
          <div className="md:col-span-2 space-y-6">
            {[
              { icon: Mail, title: 'Email Us', detail: 'info@yasconnect.co', sub: 'We respond within 24 hours', color: '#E86C3D' },
              { icon: MapPin, title: 'Location', detail: 'Newark, New Jersey', sub: 'Serving programs nationwide', color: '#3B82F6' },
              { icon: Clock, title: 'Office Hours', detail: 'Mon–Fri, 9am–6pm EST', sub: 'Weekend support by email', color: '#4ADE80' },
            ].map((c, i) => (
              <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
                className="p-6 rounded-3xl bg-white border border-black/5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c.color + '15' }}>
                    <c.icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <div>
                    <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="font-bold text-[#2D2824]">{c.title}</p>
                    <p className="text-sm text-[#2D2824] mt-0.5">{c.detail}</p>
                    <p className="text-xs text-[#2D2824]/50 mt-0.5">{c.sub}</p>
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.div {...fadeIn} transition={{ delay: 0.3 }}
              className="p-6 rounded-3xl bg-[#2D2824] text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 text-[#FFD60A]" />
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="font-bold mb-2">Want a Live Demo?</h3>
              <p className="text-xs leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
                See the platform in action with a personalized walkthrough tailored to your program type.
              </p>
              <Button onClick={() => navigate('/demo')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full text-sm font-bold w-full" style={{ transition: 'background 0.2s ease' }}>
                Request a Demo <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;
